<template>
    <VJoyWrapper class="family-with-categories" direction="column" align="flex-start" row-gap="3" no-margin>
        <VJoyWrapper direction="row" align="flex-start" column-gap="2" wrap="nowrap" no-margin>
            <FamilyIcon size="xsmall" color="neutral" :family="family.id" />
            <VJoyText tag-name="p" preset="primary-300">{{ family.label }}</VJoyText>
        </VJoyWrapper>

        <ul class="family-with-categories__categories">
            <li v-for="category in categories" :key="category.id" class="family-with-categories__category" data-testid="categories-section-category">
                <UncrawlableLink v-if="category.isUncrawlableLink" v-slot="{navigate}" custom external :to="category.href ?? ''">
                    <VJoyLink class="family-with-categories__category-link" @click="navigate">
                        {{ category.label }}
                    </VJoyLink>
                </UncrawlableLink>
                <VJoyLink v-else-if="category.href" class="family-with-categories__category-link" :href="category.href">
                    {{ category.label }}
                </VJoyLink>
                <span v-else class="family-with-categories__category-label">{{ category.label }}</span>
                <VJoyTag v-if="category.main" :label="t('profile.section.categories.primary.tag')" variant="primary" size="xsmall" />
            </li>
        </ul>
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {UncrawlableLink} from '#components';
    import {useLocalePrefix, useTranslation} from '#imports';
    import {VJoyLink, VJoyTag, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import {useProfilePageViewMode} from '~/composables/useProfilePageViewMode';
    import {useProfileStore} from '~/store/profileStore';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import type {CategorizationFamily} from '~/types/domain/Categorization';
    import {orderedCategories} from '~/types/domain/Categorization';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';
    import FamilyIcon from './FamilyIcon.vue';

    type CategoryItem = {
        id: string;
        label: string;
        href?: string;
        main?: boolean;
        isUncrawlableLink?: boolean;
    };

    const {family} = defineProps<{family: CategorizationFamily}>();

    const {t} = useTranslation();
    const {withLocalePrefix} = useLocalePrefix();
    const {profile} = storeToRefs(useProfileStore());
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const {isEditionEnabled} = useProfilePageViewMode();

    const isSearchSeoUrl = (seoUrl: string): boolean => seoUrl.startsWith('/s?q=');

    const categories = computed<CategoryItem[]>(() => {
        if (family.id === 'other') {
            const label = profile.value?.categorization?.customCategory?.trim();
            return label ? [{id: 'other', label}] : [];
        }
        return orderedCategories(family.categories).map((category) => {
            const hasLink = sectionVisibility.value[SectionsVisibilityType.SEARCH_LINKS] && category.seoUrl;
            const isUncrawlableLink = hasLink && isSearchSeoUrl(category.seoUrl);

            return {
                id: category.id,
                label: category.label,
                href: hasLink ? withLocalePrefix(category.seoUrl) : undefined,
                main: category.id === profile.value?.categorization?.preferredCategory && isEditionEnabled.value,
                isUncrawlableLink: Boolean(isUncrawlableLink),
            };
        });
    });
</script>

<style lang="scss" scoped>
    .family-with-categories {
        &__categories {
            display: flex;
            flex-direction: column;
            row-gap: var(--joy-core-spacing-2);
            list-style-type: none;
            padding: 0;
            margin: 0;
            font-size: var(--joy-font-size-primary-400);
        }

        &__category {
            display: flex;
            column-gap: var(--joy-core-spacing-1);
            align-items: center;

            &-label,
            &-link {
                font-weight: var(--joy-font-weight-bold);
            }
        }
    }
</style>
